import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

import { FilePondFile } from 'filepond';
import { cloneDeep } from 'lodash';
import {
  computed,
  reactive,
  ref,
  ComputedRef,
  inject,
} from 'vue';
import { useRoute } from 'vue-router';
import { QueryClient, useQueryClient } from 'vue-query';
import { required, requiredIf } from '@vuelidate/validators';

import { rules } from '@/entities/volta-measure/components/form/common';
import { inspectionsQueryName } from '@/entities/inspection/hooks';

import { useFormErrorMessageHandler } from '@/utils/form';
import { useForm } from '@/core/hooks/forms';
import { useCreateVoltaMeasureMutation } from '@/entities/volta-measure/hooks';

import {
  NkForm,
  NkFormItem,
  NkGroup,
  NkButton,
  NkInputNumber,
  NkGrid,
  NkCol,
  NkInputText,
  NkSelect,
  NkOption,
} from '@/core/components';

import VoltaMeasureFileDropZone
  from '@/entities/volta-measure/components/VoltaMeasureFileDropZone.vue';

import AnnotationSeveritySelect
  from '@/entities/annotation-severity/components/inputs/AnnotationSeveritySelect.vue';
import { serviceInjectionKey, ServiceInject } from '@/entities/services/keys';
import { useGetVoltaItemsQuery } from '@/entities/volta-item/hooks';
import { useI18n } from 'vue-i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'VoltaMeasureCreateForm',
  props: {
  isBladeElement: {
    type: Boolean,
    required: true,
  },
},
  emits: ['submitted'],
  setup(__props, { emit }) {

const props = __props

const { locale } = useI18n();
const route = useRoute();




const queryClient: QueryClient = useQueryClient();
const form = ref();

const { onSubmitted } = useForm(emit);

const {
  mutateAsync,
  isLoading,
  isError,
  error: createVoltaMeasureError,
} = useCreateVoltaMeasureMutation();

const { error } = useFormErrorMessageHandler(createVoltaMeasureError);

const inspectionData = computed(() => ({ id: Number(route.params.inspection) }));

const voltaItemsQueryFilters: ComputedRef = computed(() => ({ isBladeItem: props.isBladeElement }));
const { data: voltaItems, isLoading: isLoadingVoltaItems } = useGetVoltaItemsQuery({}, voltaItemsQueryFilters);

const { service, isVoltaExpress, isVoltaPlus } = inject(serviceInjectionKey) as ServiceInject;

const fields = reactive({
  type: 0,
  positionFromHub: null,
  measureValue: null,
  amperage: null,
  voltage: null,
  severity: null,
  file: '',
  voltaItem: 0,
});

const currentVoltaItem = computed(() => voltaItems.value?.find(item => item.id === fields.voltaItem));

const _rules = computed(() => ({
    measureValue: { required: requiredIf(() => currentVoltaItem.value?.isMeasureRequired ?? true) },
    positionFromHub: { required: requiredIf(() => currentVoltaItem.value?.isPositionFromHubRequired ?? true) },
    severity: { required: requiredIf(() => currentVoltaItem.value?.isSeverityRequired ?? true) },
    amperage: { required: requiredIf(() => isVoltaPlus.value && (currentVoltaItem.value?.isMeasureRequired ?? true))},
    voltage: { required: requiredIf(() => isVoltaPlus.value && (currentVoltaItem.value?.isMeasureRequired ?? true))},
    file: {
      required,
    },
}));

const handleUpload = (e: FilePondFile) => {
  fields.file = e.file;
};

const handleSubmit = () => {
  if (!isLoading.value) {
    const copy = cloneDeep(fields);
    delete copy.file;

    const formData: FormData = new FormData();
    formData.append('file', fields.file);
    formData.append('data', JSON.stringify({
      ...copy,
      inspection: inspectionData.value.id,
    }));

    form.value.validator.$validate().then((valid: boolean) => {
      console.log(valid);
      if (valid) {
        mutateAsync(formData).then((data) => {
          onSubmitted(data);
          queryClient.refetchQueries([inspectionsQueryName, { id: inspectionData.value.id }], { active: true });
        });
      }
    });
  }
};

return (_ctx: any,_cache: any) => {
  const _component_ElAlert = _resolveComponent("ElAlert")!
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createBlock(_unref(NkForm), {
    ref_key: "form",
    ref: form,
    fields: fields,
    rules: _unref(isVoltaExpress) ? _unref(rules) : _unref(_rules)
  }, {
    header: _withCtx(() => [
      (_unref(isError))
        ? (_openBlock(), _createBlock(_component_ElAlert, {
            key: 0,
            "show-icon": "",
            title: _unref(error),
            type: "error"
          }, null, 8, ["title"]))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(NkGroup), { position: "right" }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkButton), {
            disabled: _unref(isLoading),
            type: "primary",
            onClick: handleSubmit
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('actions.create')), 1)
            ]),
            _: 1
          }, 8, ["disabled"])
        ]),
        _: 1
      })
    ]),
    default: _withCtx(() => [
      _createVNode(_unref(NkGrid), null, {
        default: _withCtx(() => [
          _createVNode(_unref(NkCol), { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkFormItem), {
                prop: "voltaItem",
                label: _ctx.$t('volta-measure.form.item.label')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkSelect), {
                    modelValue: fields.voltaItem,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((fields.voltaItem) = $event)),
                    isDisabled: _unref(isLoadingVoltaItems)
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(voltaItems), (voltaItem) => {
                        return (_openBlock(), _createBlock(_unref(NkOption), {
                          key: voltaItem.id,
                          value: voltaItem.id,
                          label: voltaItem.name[_unref(locale)]
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(voltaItem.name[_unref(locale)]), 1)
                          ]),
                          _: 2
                        }, 1032, ["value", "label"]))
                      }), 128))
                    ]),
                    _: 1
                  }, 8, ["modelValue", "isDisabled"])
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }),
          _createVNode(_unref(NkCol), { span: 12 }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkFormItem), {
                prop: "positionFromHub",
                label: _ctx.$t('volta-measure.form.positionFromHub.label')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkInputNumber), {
                    modelValue: fields.positionFromHub,
                    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((fields.positionFromHub) = $event))
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }),
          _createVNode(_unref(NkCol), { span: 6 }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkFormItem), {
                prop: "severity",
                label: _ctx.$t('misc.severity')
              }, {
                default: _withCtx(() => [
                  (_unref(service).id)
                    ? (_openBlock(), _createBlock(AnnotationSeveritySelect, {
                        key: 0,
                        modelValue: fields.severity,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((fields.severity) = $event)),
                        service: _unref(service).id
                      }, null, 8, ["modelValue", "service"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }),
          _createVNode(_unref(NkCol), { span: 6 }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkFormItem), {
                prop: "measuredValue",
                label: _ctx.$t('volta-measure.form.measuredValue.label')
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkInputText), {
                    modelValue: fields.measureValue,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((fields.measureValue) = $event))
                  }, null, 8, ["modelValue"])
                ]),
                _: 1
              }, 8, ["label"])
            ]),
            _: 1
          }),
          (_unref(isVoltaPlus))
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_unref(NkCol), { span: 6 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NkFormItem), {
                      prop: "amperage",
                      label: _ctx.$t('volta-measure.form.amperage.label')
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(NkInputText), {
                          modelValue: fields.amperage,
                          "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((fields.amperage) = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                }),
                _createVNode(_unref(NkCol), { span: 6 }, {
                  default: _withCtx(() => [
                    _createVNode(_unref(NkFormItem), {
                      prop: "voltage",
                      label: _ctx.$t('volta-measure.form.voltage.label')
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(NkInputText), {
                          modelValue: fields.voltage,
                          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((fields.voltage) = $event))
                        }, null, 8, ["modelValue"])
                      ]),
                      _: 1
                    }, 8, ["label"])
                  ]),
                  _: 1
                })
              ], 64))
            : _createCommentVNode("", true),
          (!_unref(isVoltaExpress))
            ? (_openBlock(), _createBlock(_unref(NkCol), {
                key: 1,
                span: 12
              }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkFormItem), {
                    prop: "file",
                    label: _ctx.$t('misc.photo')
                  }, {
                    default: _withCtx(() => [
                      _createVNode(VoltaMeasureFileDropZone, {
                        onAddFile: handleUpload,
                        onRemoveFile: _cache[6] || (_cache[6] = ($event: any) => (fields.file = ''))
                      })
                    ]),
                    _: 1
                  }, 8, ["label"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["fields", "rules"])), [
    [_directive_loading, _unref(isLoading)]
  ])
}
}

})